import * as THREE from 'three'
import Terrain, {
  BlockType,
  BiomeType,
  getBiomeType,
  getSurfaceBlockType
} from '../../terrain'

const RANGE = 40
const CELL = 2
const SIZE = RANGE * 2 * CELL
const WATER_LEVEL = 28
const REDRAW_GAP = 150

// top-down color per block type, indexed by BlockType
const COLORS: Array<[number, number, number]> = [
  [111, 170, 60], // grass
  [219, 207, 163], // sand
  [102, 81, 50], // tree
  [46, 125, 45], // leaf
  [134, 96, 67], // dirt
  [126, 126, 126], // stone
  [58, 58, 58], // coal
  [156, 127, 78], // wood
  [97, 219, 213], // diamond
  [235, 229, 222], // quartz
  [190, 225, 235], // glass
  [48, 48, 48], // bedrock
  [216, 203, 155], // sandstone
  [64, 148, 62], // cactus
  [85, 67, 25], // jungleTree
  [38, 110, 50], // jungleLeaf
  [150, 110, 70], // jungleWood
  [63, 118, 228], // water
  [145, 130, 120], // ironOre
  [110, 205, 200], // diamondOre
  [100, 100, 100], // furnace
  [139, 90, 43] // chest
]

/**
 * Top-down rectangular map around the player
 */
export default class Minimap {
  constructor(terrain: Terrain, camera: THREE.PerspectiveCamera) {
    this.terrain = terrain
    this.camera = camera
    this.canvas.className = 'minimap'
    this.canvas.classList.add('hidden')
    this.canvas.width = SIZE
    this.canvas.height = SIZE
    document.body.appendChild(this.canvas)
  }

  terrain: Terrain
  camera: THREE.PerspectiveCamera
  canvas = document.createElement('canvas')
  active = false
  lastDraw = 0
  direction = new THREE.Vector3()

  setActive(active: boolean) {
    this.active = active
    this.canvas.classList.toggle('hidden', !active)
  }

  update = () => {
    if (!this.active) {
      return
    }
    const now = performance.now()
    if (now - this.lastDraw < REDRAW_GAP) {
      return
    }
    this.lastDraw = now
    this.draw()
  }

  draw = () => {
    const ctx = this.canvas.getContext('2d')
    if (!ctx) {
      return
    }

    const noise = this.terrain.noise
    const cx = Math.round(this.camera.position.x)
    const cz = Math.round(this.camera.position.z)

    // custom blocks overlay inside visible range
    const placed = new Map<string, { y: number; type: BlockType }>()
    const removed = new Set<string>()
    for (const block of this.terrain.customBlocks) {
      if (
        block.x < cx - RANGE ||
        block.x > cx + RANGE ||
        block.z < cz - RANGE ||
        block.z > cz + RANGE
      ) {
        continue
      }
      if (block.placed) {
        const key = `${block.x}_${block.z}`
        const current = placed.get(key)
        if (!current || block.y > current.y) {
          placed.set(key, { y: block.y, type: block.type })
        }
      } else {
        removed.add(`${block.x}_${block.y}_${block.z}`)
      }
    }

    for (let dx = -RANGE; dx < RANGE; dx++) {
      for (let dz = -RANGE; dz < RANGE; dz++) {
        const x = cx + dx
        const z = cz + dz

        const yOffset = Math.floor(
          noise.get(x / noise.gap, z / noise.gap, noise.seed) * noise.amp
        )
        const stoneOffset =
          noise.get(x / noise.stoneGap, z / noise.stoneGap, noise.stoneSeed) *
          noise.stoneAmp
        const coalOffset =
          noise.get(x / noise.coalGap, z / noise.coalGap, noise.coalSeed) *
          noise.coalAmp

        let y = 30 + yOffset
        let type = getSurfaceBlockType(
          x,
          z,
          yOffset,
          stoneOffset,
          coalOffset,
          noise
        )

        if (y < WATER_LEVEL) {
          type = BlockType.water
        } else {
          const treeOffset =
            noise.get(x / noise.treeGap, z / noise.treeGap, noise.treeSeed) *
            noise.treeAmp
          const jungle = getBiomeType(x, z) === BiomeType.jungle
          const treeThreshold = jungle
            ? noise.treeThreshold - 1.2
            : noise.treeThreshold
          if (
            treeOffset > treeThreshold &&
            yOffset >= -3 &&
            stoneOffset < noise.stoneThreshold
          ) {
            type = jungle ? BlockType.jungleLeaf : BlockType.leaf
          }
        }

        // removed surface block: show the layer below
        if (type !== BlockType.water && removed.has(`${x}_${y}_${z}`)) {
          type =
            stoneOffset > noise.stoneThreshold
              ? BlockType.stone
              : BlockType.dirt
        }

        // placed block on top of the column
        const top = placed.get(`${x}_${z}`)
        if (top && top.y >= y) {
          y = top.y
          type = top.type
        }

        const color = COLORS[type] ?? COLORS[BlockType.stone]
        const shade = Math.min(1.2, Math.max(0.55, 0.75 + (y - 30) * 0.035))
        ctx.fillStyle = `rgb(${(color[0] * shade) | 0}, ${
          (color[1] * shade) | 0
        }, ${(color[2] * shade) | 0})`
        ctx.fillRect((dx + RANGE) * CELL, (dz + RANGE) * CELL, CELL, CELL)
      }
    }

    // player marker rotated by camera yaw
    this.direction.set(0, 0, -1).applyQuaternion(this.camera.quaternion)
    const angle = Math.atan2(this.direction.x, -this.direction.z)
    ctx.save()
    ctx.translate(SIZE / 2, SIZE / 2)
    ctx.rotate(angle)
    ctx.beginPath()
    ctx.moveTo(0, -7)
    ctx.lineTo(5, 6)
    ctx.lineTo(-5, 6)
    ctx.closePath()
    ctx.fillStyle = '#ffffff'
    ctx.strokeStyle = '#000000'
    ctx.fill()
    ctx.stroke()
    ctx.restore()
  }
}
